import {useInjection} from "inversify-react";
import {useEffect, useState} from "react";
import ApiService from "../../services/apiService";
import {
    CategoryApi,
    CategoryDto,
    Direction,
    ProductApi,
    ProductCreateDto,
    ProductDto, ProductImageApi, ProductImageDto,
    ProductUpdateDto
} from "../../api";
import ProductsList from "./productList";
import ProductModalFormAddContainer from "./productModalFormAddContainer/productModalFormAddContainer";
import {Button, Layout, notification} from "antd";
import {Content} from "antd/lib/layout/layout";
import styles from "./productListContainer.module.scss";
import {useNavigate, useParams} from "react-router-dom";
import {UploadFile} from "antd/lib/upload/interface";
import ProductSelectModalForm from "./productModalForm/productSelectModalForm";

export default function ProductsListContainer() {
    const navigate = useNavigate();
    const apiService = useInjection(ApiService);
    const [products, setProducts] = useState<ProductDto[] | null>();
    const [isAddFormVisible, setAddFormVisible] = useState(false);
    const [productModalFormContainerTitle, setProductModalFormContainerTitle] = useState<string>();
    const [productToEdit, setProductToEdit] = useState<ProductUpdateDto | null>(null);
    const [idProductToEdit, setIdProductToEdit] = useState<number | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [initCategoryIds, setInitCategoryIds] = useState<number[]>([]);
    const [productImages, setProductImages] = useState<ProductImageDto[] | null>();
    const [files, setFiles] = useState<UploadFile<any>[]>([]);
    const [otherProducts, setOtherProducts] = useState<ProductDto[]>([]);
    const [isAddExistingFormVisible, setAddExistingFormVisible] = useState(false);
    const [categories, setCategories] = useState<CategoryDto[] | null>();
    const {categoryId} = useParams();

    const refreshProductsList = async () => {
        setIsLoading(true);

        try {
            const productsByCategory = await apiService.getApi(ProductApi).apiProductProductCategoryLinksGet();
            const allProducts = await apiService.getApi(ProductApi).apiProductGet();
            const productsData = productsByCategory.data.filter(p => p.categoryId === +categoryId!).sort((a, b) => {
                return a.productPosition! - b.productPosition!;
            }).map(p => p.product!)
            const categories = await apiService.getApi(CategoryApi).apiCategoryGet();

            setProducts(productsData);
            setOtherProducts(allProducts.data.filter(item => {
                return !productsData.some(field => item.id === field.id)
            }));
            setCategories(categories.data);
        } catch (e: any) {
            console.log(e.response.data);
        }

        setIsLoading(false);
    }

    useEffect(() => {
        refreshProductsList();
    }, [])

    const onAddClicked = () => {
        setProductModalFormContainerTitle("Dodaj nowy produkt");
        setProductImages([]);
        setInitCategoryIds([+categoryId!])
        setProductToEdit(null);
        setIdProductToEdit(null);
        setAddFormVisible(true);
    }

    const onEditClicked = async (id: number) => {
        setProductModalFormContainerTitle(`Edytuj produkt o ID: ${id}`);
        setIsLoading(true);

        const response = await apiService.getApi(ProductApi).apiProductIdGet(id);

        const ProductImages = await apiService.getApi(ProductImageApi).apiProductImageProductImagesProductIdGet(id);
        setProductImages(ProductImages.data.sort((a, b) => {
            return a.position! - b.position!;
        }));

        setProductToEdit(response.data as ProductUpdateDto);
        setIdProductToEdit(id);
        setAddFormVisible(true);
        setIsLoading(false);
    }

    const onDeleteClicked = async (id: number) => {
        const response = await apiService.getApi(ProductApi).apiProductProductIdSoftDeleteCategoryIdDelete(id, +categoryId!)

        if (response.request.status !== 200) {
            console.log("Error occured while deleting data"); //add some warning?
        } else {
            refreshProductsList();
        }
    }

    const openNotification = (message: string) => {
        notification.info({
            message: message,
            description:
                'Aby wprowadzone zmiany były widoczne na stronie konieczna jest publikacja',
            placement: "top"
        });
    };

    const onSubmit = async (values: ProductCreateDto | ProductUpdateDto) => {
        try {
            if (productToEdit === null) {
                if (!values.categoriesIds?.length || !values.categoriesIds?.find(x => x === +categoryId!)) {
                    values.categoriesIds = [...(values.categoriesIds ?? []), +categoryId!]
                }
                await apiService.getApi(ProductApi).apiProductPost(values);
                openNotification("Pomyślnie dodano nowy wpis");
            } else if (idProductToEdit !== null) {
                values.imagesIds = files.map(f => +f.uid)
                await apiService.getApi(ProductApi).apiProductIdPut(idProductToEdit, values)
                openNotification("Pomyślnie zaktualizowano wpis");
            }
        } catch (e: any) {
            return e.response?.data;
        }

        setAddFormVisible(false)
        refreshProductsList();
    }

    const onPositionChangeClicked = async (id: number, direction: Direction) => {
        setIsLoading(true);

        try {
            const productsByCategory = await apiService.getApi(ProductApi).apiProductPositionIdCategoryIdDirectionPatch(id, +categoryId!, direction);
            const products = productsByCategory.data.filter(p => p.categoryId === +categoryId!).sort((a, b) => {
                return a.productPosition! - b.productPosition!;
            }).map(p => p.product!)
            setProducts(products);
        } catch (e: any) {
            console.log(e.response.data);
        }

        setIsLoading(false);
    }

    let onAddExistingProductClicked = () => {
        setProductToEdit(null);
        setIdProductToEdit(null);
        setAddExistingFormVisible(true);
    }

    let onExistingProductSubmit = async (values: ProductUpdateDto | ProductCreateDto) => {
        const response = await apiService.getApi(ProductApi).apiProductIdGet(values.id!);

        try {
            const updateDto = response.data as ProductUpdateDto;
            updateDto.categoriesIds?.push(+categoryId!);
            await apiService.getApi(ProductApi).apiProductIdPut(updateDto.id!, updateDto);
        } catch (e: any) {
            return e.response.data;
        }
        setAddExistingFormVisible(false);
        await refreshProductsList();
    }

    return <>
        <Layout style={{backgroundColor: "white"}}>
            <Content>
                <Layout>
                    <Content className={styles.content}>
                        <ProductsList
                            data={products || null}
                            onAddClicked={onAddClicked}
                            onEditClicked={onEditClicked}
                            onDeleteClicked={onDeleteClicked}
                            onPositionChangeClicked={onPositionChangeClicked}
                            onAddExistingClicked={onAddExistingProductClicked}
                        />
                        <ProductModalFormAddContainer
                            onFinish={() => setAddFormVisible(false)}
                            onSubmit={onSubmit}
                            addFormVisibility={isAddFormVisible}
                            title={productModalFormContainerTitle || ""}
                            ProductToEdit={productToEdit}
                            initCategoryId={initCategoryIds}
                            categories={categories!}
                            productImages={productImages!}
                            files={files}
                            setFiles={setFiles}
                        />
                        <ProductSelectModalForm
                            isModalVisible={isAddExistingFormVisible}
                            setModalVisibility={() => setAddExistingFormVisible(false)}
                            onSubmit={onExistingProductSubmit}
                            modalTitle={"Dodaj istniejący produkt"}
                            saveButtonTitle={"Dodaj"}
                            options={otherProducts}/>
                        {categoryId && <Button
                            style={{marginTop: "1rem"}}
                            onClick={() => navigate(-1)}
                        >
                            Wróć
                        </Button>}
                    </Content>
                </Layout>
            </Content>
        </Layout>
    </>
}

